class NotificationSoundService {
  private audioContext: AudioContext | null = null;
  private soundEnabled: boolean = true;

  constructor() {
    this.initializeAudio();
  }

  private async initializeAudio() {
    try {
      // Create audio context on first user interaction
      document.addEventListener('click', this.createAudioContext.bind(this), { once: true });
      document.addEventListener('touchstart', this.createAudioContext.bind(this), { once: true });
    } catch (error) {
      console.warn('[NotificationSoundService] Audio initialization failed:', error);
    }
  }

  private createAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }

  setSoundEnabled(enabled: boolean) {
    this.soundEnabled = enabled;
    localStorage.setItem('flamingo_notification_sound_enabled', enabled.toString());
  }

  isSoundEnabled(): boolean {
    const stored = localStorage.getItem('flamingo_notification_sound_enabled');
    return stored !== null ? stored === 'true' : this.soundEnabled;
  }

  private createBeepSound(frequency: number = 800, duration: number = 200, volume: number = 0.1) {
    if (!this.audioContext || !this.isSoundEnabled()) return;

    try {
      // Create oscillator for tone
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      // Configure oscillator
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;

      // Configure volume envelope
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, this.audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration / 1000);

      // Connect and play
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration / 1000);
    } catch (error) {
      console.warn('[NotificationSoundService] Failed to play beep:', error);
    }
  }

  // Play notification sound for different types
  playNotificationSound(type: 'match' | 'message' | 'application' | 'tour' | 'property_like' | 'system' | 'error' = 'system') {
    if (!this.isSoundEnabled()) return;

    this.createAudioContext(); // Ensure audio context exists

    switch (type) {
      case 'match':
        // Higher pitched, celebratory sound
        this.createBeepSound(1000, 150, 0.15);
        setTimeout(() => this.createBeepSound(1200, 150, 0.1), 200);
        break;
      case 'message':
        // Medium pitched, friendly sound  
        this.createBeepSound(800, 200, 0.12);
        break;
      case 'application':
        // Professional, important sound
        this.createBeepSound(600, 300, 0.15);
        break;
      case 'tour':
        // Friendly appointment sound
        this.createBeepSound(700, 250, 0.12);
        break;
      case 'property_like':
        // Positive feedback sound
        this.createBeepSound(900, 180, 0.1);
        break;
      case 'error':
        // Lower pitched, alert sound
        this.createBeepSound(400, 400, 0.2);
        break;
      default:
        // Default notification sound
        this.createBeepSound(800, 200, 0.1);
    }
  }

  // Play success sound for actions
  playSuccessSound() {
    if (!this.isSoundEnabled()) return;
    
    this.createAudioContext();
    this.createBeepSound(800, 100, 0.08);
    setTimeout(() => this.createBeepSound(1000, 100, 0.06), 150);
  }

  // Play error sound
  playErrorSound() {
    if (!this.isSoundEnabled()) return;
    
    this.createAudioContext();
    this.createBeepSound(300, 300, 0.15);
  }
}

export const notificationSoundService = new NotificationSoundService();